import { cn } from '@/lib/utils';

// ── Sparkline (inline mini chart) ──
export function Sparkline({ data, width = 80, height = 24, color = 'var(--accent)' }: {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
}) {
  if (data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x},${y}`;
  });
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline points={points.join(' ')} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Area Chart ──
export function AreaChart({ data, series, height = 160 }: {
  data: { date: string }[];
  series: { key: string; color: string; label: string }[];
  height?: number;
}) {
  const width = 600;
  const padding = { top: 10, right: 10, bottom: 20, left: 30 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const allValues = series.flatMap((s) => data.map((d) => (d as any)[s.key]));
  const max = Math.max(...allValues, 1);

  const xStep = innerW / Math.max(data.length - 1, 1);

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height }}>
        {series.map((s) => {
          const points = data.map((d, i) => {
            const x = padding.left + i * xStep;
            const y = padding.top + innerH - ((d as any)[s.key] / max) * innerH;
            return `${x},${y}`;
          });
          const areaPoints = `${padding.left},${padding.top + innerH} ${points.join(' ')} ${padding.left + innerW},${padding.top + innerH}`;
          return (
            <g key={s.key}>
              <polygon points={areaPoints} fill={s.color} fillOpacity="0.08" />
              <polyline points={points.join(' ')} fill="none" stroke={s.color} strokeWidth="1.5" strokeLinecap="round" />
            </g>
          );
        })}
        {Array.from({ length: 5 }).map((_, i) => {
          const y = padding.top + (innerH / 4) * i;
          return <line key={i} x1={padding.left} y1={y} x2={padding.left + innerW} y2={y} stroke="var(--border)" strokeWidth="0.5" />;
        })}
      </svg>
      <div className="flex items-center gap-4 mt-2 px-2">
        {series.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5 text-xs text-text-muted">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Donut Chart ──
export function DonutChart({ data, size = 120, thickness = 16 }: {
  data: { label: string; value: number; color: string }[];
  size?: number;
  thickness?: number;
}) {
  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="flex items-center gap-4">
      <svg width={size} height={size} className="shrink-0">
        {data.map((d) => {
          const fraction = d.value / total;
          const dash = fraction * circumference;
          const circle = (
            <circle
              key={d.label}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={d.color}
              strokeWidth={thickness}
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          );
          offset += dash;
          return circle;
        })}
        <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="middle" className="fill-text-primary text-lg font-semibold">
          {total}
        </text>
      </svg>
      <div className="space-y-1.5">
        {data.map((d) => (
          <div key={d.label} className="flex items-center gap-2 text-xs">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: d.color }} />
            <span className="text-text-secondary">{d.label}</span>
            <span className="text-text-muted tabular ml-auto">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Bar Chart (horizontal) ──
export function BarChart({ data, height }: {
  data: { label: string; value: number; color?: string }[];
  height?: number;
}) {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="space-y-2" style={{ minHeight: height }}>
      {data.map((d) => (
        <div key={d.label} className="flex items-center gap-3">
          <span className="text-xs text-text-secondary w-24 truncate">{d.label}</span>
          <div className="flex-1 h-5 bg-base-bg rounded overflow-hidden">
            <div
              className="h-full rounded transition-all duration-300"
              style={{ width: `${(d.value / max) * 100}%`, backgroundColor: d.color || 'var(--accent)' }}
            />
          </div>
          <span className="text-xs text-text-muted tabular w-8 text-right">{d.value}</span>
        </div>
      ))}
    </div>
  );
}

// ── Progress bar ──
export function ProgressBar({ value, max = 100, color, height = 6, showLabel }: {
  value: number;
  max?: number;
  color?: string;
  height?: number;
  showLabel?: boolean;
}) {
  const pct = Math.min((value / max) * 100, 100);
  const c = color || (pct > 85 ? 'var(--status-critical)' : pct > 70 ? 'var(--status-warning)' : 'var(--status-online)');
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 bg-base-hover rounded overflow-hidden" style={{ height }}>
        <div className="h-full rounded transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: c }} />
      </div>
      {showLabel && <span className="text-xs text-text-muted tabular">{Math.round(pct)}%</span>}
    </div>
  );
}
